import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { oktaAuthInstance } from './services/okta-config';
import { useSelector, useDispatch } from 'react-redux';
import Map from './pages/map';
import Promotions from './pages/promotions';
import EmailAdValidation from './pages/emailAdValidation';
import CreatePromotions from './pages/createPromotion/components/CreatePromotion';
import { ROUTES } from './constants/routes';
import Login from './pages/login';
import OktaLogin from './pages/oktaLogin';
import ForgotPassword from './pages/forgotPassword';
import CommonComponentDemo from './pages/commonComponentDemo';
import SpendAndOpportunities from './pages/spendAndOpportunitiesScreen/components/SpendAndOppurtunities';
import ActualSpend from './pages/actualSpend';
import CurrentPlanSpend from './pages/currentPlanSpend';
import AudienceAnalysis from './pages/audienceAnalysis';
import PaidSearch from './pages/paidSearch';
import Layout from './components/Layout';
import ResetPassword from './pages/resetPassword';
import { getUserDetails } from './modules/actions/authActions';
import OverallPerformance from './pages/overallPerformance';
import PerformanceOverview from './pages/PerformanceOverview';
import OpportunitiesInsights from './pages/OpportunitiesInsights';
import OpportunitieByChannel from './pages/opportunitiesByChannel/components/OpportunitieByChannel';
import Creatives from './pages/creatives/components/Creative';
import ActualVsBudget from './pages/actualVsBudget/components/ActualVsBudget';
import { setRedirectUrlAfterLogin } from './modules/reducer/authSlice';
import { findFirstValidUrl, isRouteInMenu } from './constants/utils';
import { InitialPlanSpend } from './pages/initialPlanSpend/components/InitialPlanSpend';
import { OppsByChannelSubBranch } from './pages/opportunitiesByChannelSubBranch/OppsByChannelSubBranch';
import { OverallPerformanceSubBranch } from './pages/overallPerformanceSubBranch/overallPerformanceSubBranch';
import { PeformanceOverviewSubBranch } from './pages/perforamnceOverviewSubBranch/PerformancOverViewSubBranch';
import { InitialPlanSpendOpps } from './pages/initialPlanSpendOpps/components/InitialPalnSpendOpps';

export const AppRoutes = () => {
  const { isUserLoggedIn, userData, userAccessToken, redirectUrlAfterLogin } = useSelector(
    (state) => state.auth
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const oktaAuth = oktaAuthInstance;

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath =
      searchParams.get('redirect') || originalUri || ROUTES.executiveSummaryOverallPerformance;
    navigate(toRelativeUrl(redirectPath, window.location.origin), { replace: true });
  };

  useEffect(() => {
    if (userAccessToken && Object.keys(userData).length === 0) {
      dispatch(getUserDetails());
    }
  }, [userAccessToken, userData, dispatch]);

  const redirectToLogin = () => {
    if (![ROUTES.login, ROUTES.oktaLogin]?.some((path) => location?.pathname?.includes(path))) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    }
    const redirectPath = `${location.pathname}${location.search}`;
    return <Navigate to={`${ROUTES.login}?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  };

  useEffect(() => {
    const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');
    if (redirectAfterLogin && redirectAfterLogin !== ROUTES.login) {
      try {
        dispatch(setRedirectUrlAfterLogin(redirectAfterLogin));
      } catch (error) {}
    }
  }, []);

  return (
    <Security restoreOriginalUri={restoreOriginalUri} oktaAuth={oktaAuth}>
      {isUserLoggedIn ? (
        <Layout>
          <Routes>
            {isRouteInMenu(userData?.data?.menu, ROUTES.map) && (
              <Route path={ROUTES.map} element={<Map />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.executiveSummaryOverallPerformance) && (
              <Route
                path={ROUTES.executiveSummaryOverallPerformance}
                element={<OverallPerformance />}
              />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.subBranchOverallPerformance) && (
              <Route
                path={ROUTES.subBranchOverallPerformance}
                element={<OverallPerformanceSubBranch />}
              />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.promotions) && (
              <Route path={ROUTES.promotions} element={<Promotions />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.emailAdValidation) && (
              <Route path={ROUTES.emailAdValidation} element={<EmailAdValidation />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.creatives) && (
              <Route path={ROUTES.creatives} element={<Creatives />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.promotions) && (
              <Route path={ROUTES.createPromotions} element={<CreatePromotions />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.promotions) && (
              <Route path={ROUTES.editPromotions} element={<CreatePromotions />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.commonComponentDemo) && (
              <Route path={ROUTES.commonComponentDemo} element={<CommonComponentDemo />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.executiveSummarySpendAndOpportunities) && (
              <Route
                path={ROUTES.executiveSummarySpendAndOpportunities}
                element={<SpendAndOpportunities />}
              />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.spendActual) && (
              <Route path={ROUTES.spendActual} element={<ActualSpend />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.spendCurrentPlan) && (
              <Route path={ROUTES.spendCurrentPlan} element={<CurrentPlanSpend />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.spendInitialPlanSPend) && (
              <Route path={ROUTES.spendInitialPlanSPend} element={<InitialPlanSpend />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.oppsInitialPlanSpend) && (
              <Route path={ROUTES.oppsInitialPlanSpend} element={<InitialPlanSpendOpps />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.wipAudienceAnalysis) && (
              <Route path={ROUTES.wipAudienceAnalysis} element={<AudienceAnalysis />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.opportunitiesByChannelLastTouch) && (
              <Route
                path={ROUTES.opportunitiesByChannelLastTouch}
                element={<OpportunitieByChannel />}
              />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.subBranchOppsByChannel) && (
              <Route path={ROUTES.subBranchOppsByChannel} element={<OppsByChannelSubBranch />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.subBranchPerformanceOverview) && (
              <Route
                path={ROUTES.subBranchPerformanceOverview}
                element={<PeformanceOverviewSubBranch />}
              />
            )}
            {userData?.data?.is_gale_admin &&
              isRouteInMenu(userData?.data?.menu, ROUTES.spendActualVsBudget) && (
                <Route path={ROUTES.spendActualVsBudget} element={<ActualVsBudget />} />
              )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.paidSearch) && (
              <Route path={ROUTES.paidSearch} element={<PaidSearch />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.performanceOverview) && (
              <Route path={ROUTES.performanceOverview} element={<PerformanceOverview />} />
            )}
            {isRouteInMenu(userData?.data?.menu, ROUTES.opportunitiesInsights) && (
              <Route path={ROUTES.opportunitiesInsights} element={<OpportunitiesInsights />} />
            )}
            {isRouteInMenu(userData?.data?.menu, redirectUrlAfterLogin) && (
              <Route path="*" element={<Navigate to={redirectUrlAfterLogin} />} />
            )}
            {userData?.data?.menu?.length &&
              !isRouteInMenu(userData?.data?.menu, redirectUrlAfterLogin) && (
                <Route
                  path="*"
                  element={<Navigate to={findFirstValidUrl(userData?.data?.menu)} />}
                />
              )}
          </Routes>
        </Layout>
      ) : (
        <Routes>
          <Route path={ROUTES.login} element={<Login />} />
          <Route path={ROUTES.oktaLogin} element={<OktaLogin />} />
          <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
          <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
          <Route path="*" element={redirectToLogin()} />
        </Routes>
      )}
    </Security>
  );
};
